import { useEffect, useRef } from 'react';
import { isFinancialOpsReady } from '../../../infrastructure/startup/startupIntegritySignals';
import {
  runAutomaticDailyClosingCheck,
  scheduleLocalMidnightDailyClosing,
  BUSINESS_LAZY_LOAD_COMPLETE_EVENT,
  DAILY_CLOSING_SAFETY_INTERVAL_MS,
  PERIODIC_SYNC_INTERVAL_MS,
  BUSINESS_STATUS_INTERVAL_MS
} from '../../../domain/operations/dailyClosingOrchestrator';
import {
  drainPendingSyncOnce,
  runElectronOnlineSyncWithRetry,
  createElectronSyncRetryRunner
} from '../../../domain/sync/electronSyncOrchestrator';

/**
 * Periodischer Sync, Business-Status, Tagesabschluss, Online/Electron-Handler.
 * Single cloud entry: scheduleAutomaticCloudSync (mirror pull + hybrid + push/drain).
 */
export function useBackgroundSyncLifecycle({
  enabled,
  getClosingData,
  checkBusinessStatus,
  businessId = null
}) {
  const syncRetryRunnerRef = useRef(null);
  const startupClosingDoneRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      startupClosingDoneRef.current = false;
      return undefined;
    }

    checkBusinessStatus();
    const statusInterval = setInterval(checkBusinessStatus, BUSINESS_STATUS_INTERVAL_MS);

    const flushPendingCloudRegister = () => {
      void import('../../../services/upgradeLocalBusinessToCloud')
        .then(({ tryFlushPendingLocalCloudRegisters }) =>
          tryFlushPendingLocalCloudRegisters()
        )
        .catch(() => {});
    };

    const triggerAutoCloud = (reason) => {
      flushPendingCloudRegister();
      void import('../../../services/parallelCloudFieldSync')
        .then(({ scheduleAutomaticCloudSync }) => {
          scheduleAutomaticCloudSync(businessId, { reason });
        })
        .catch(() => {});
    };

    const runVisiblePeriodicSync = (reason = 'periodic') => {
      if (!isFinancialOpsReady()) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void drainPendingSyncOnce(40).catch(() => {});
      triggerAutoCloud(reason);
    };

    // One orchestrated cycle at boot
    triggerAutoCloud('boot');

    const syncInterval = setInterval(() => {
      runVisiblePeriodicSync('periodic');
    }, PERIODIC_SYNC_INTERVAL_MS);

    const handleVisibilityChange = () => {
      // Catch up after long hidden tab — next 60s tick alone can lag outbox/mirror.
      if (document.visibilityState === 'visible') {
        runVisiblePeriodicSync('visible');
      }
    };

    const checkAutomaticDailyClosing = async () => {
      try {
        await runAutomaticDailyClosingCheck();
      } catch (error) {
        console.error('❌ [DAILY CLOSING] Fehler beim automatischen Tagesabschluss-Check:', error);
      }
    };

    if (!startupClosingDoneRef.current) {
      startupClosingDoneRef.current = true;
      void checkAutomaticDailyClosing();
    }

    const cancelMidnight = scheduleLocalMidnightDailyClosing(checkAutomaticDailyClosing);

    const dailyClosingSafetyInterval = setInterval(
      () => void checkAutomaticDailyClosing(),
      DAILY_CLOSING_SAFETY_INTERVAL_MS
    );

    const handleLazyLoadComplete = () => {
      void checkAutomaticDailyClosing();
      triggerAutoCloud('boot');
    };
    window.addEventListener(BUSINESS_LAZY_LOAD_COMPLETE_EVENT, handleLazyLoadComplete);

    const handleOnline = () => {
      // Local→cloud account push must run even before financial ops ready.
      flushPendingCloudRegister();
      if (!isFinancialOpsReady()) return;
      checkBusinessStatus();
      triggerAutoCloud('online');
    };

    const handleElectronOnline = () => {
      flushPendingCloudRegister();
      if (!isFinancialOpsReady()) return;
      checkBusinessStatus();
      triggerAutoCloud('online');

      syncRetryRunnerRef.current?.cancelAll();
      const syncRetryRunner = createElectronSyncRetryRunner();
      syncRetryRunnerRef.current = syncRetryRunner;
      runElectronOnlineSyncWithRetry({ retryRunner: syncRetryRunner });
    };

    const handleFocus = () => {
      checkBusinessStatus();
      if (!isFinancialOpsReady()) return;
      triggerAutoCloud('online');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (window.electronAPI?.onOnline) {
      window.electronAPI.onOnline(handleElectronOnline);
    }

    return () => {
      startupClosingDoneRef.current = false;
      syncRetryRunnerRef.current?.cancelAll();
      syncRetryRunnerRef.current = null;
      cancelMidnight();
      clearInterval(statusInterval);
      clearInterval(syncInterval);
      clearInterval(dailyClosingSafetyInterval);
      window.removeEventListener(BUSINESS_LAZY_LOAD_COMPLETE_EVENT, handleLazyLoadComplete);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (window.electronAPI?.removeOnlineListener) {
        window.electronAPI.removeOnlineListener();
      }
    };
  }, [enabled, getClosingData, checkBusinessStatus, businessId]);
}
